import type { Ref } from 'vue';
import type { BlockEditorBounds } from '#layers/block-engine/composables/block-editor/bounds';
import type { BlockEditorChildParentMap } from '#layers/block-engine/composables/block-editor/child-parent-map';
import type { useBlockUpdate } from '#layers/block-engine/composables/block-editor/update';
import type { ProcessMoveBlockOptions } from '#layers/block-engine/composables/block-editor/utils/process-move-block';
import type { BlockSerialized } from '#layers/block-engine/types/block';
import type { BlockEditorContext } from '#layers/block-engine/types/block-editor';
import { useBlockLibraryStore } from '#layers/block-engine/composables/block-editor/library';
import { processMoveBlock } from '#layers/block-engine/composables/block-editor/utils/process-move-block';
import { isBlockSerializedChild } from '#layers/block-engine/utils/block/editor';
import { isBlockLocked } from '#layers/block-engine/utils/block/lock';

export function useBlockEditorMove({
	blockData,
	updateBlock,
	childParentMap,
	bounds,
	getContext,
}: {
	blockData: Ref<BlockSerialized | null>;
	updateBlock: ReturnType<typeof useBlockUpdate>['updateBlock'];
	childParentMap: BlockEditorChildParentMap;
	bounds: BlockEditorBounds;
	getContext: () => BlockEditorContext;
}) {
	const { getBlock } = useBlockLibraryStore();

	/**
	 * Move the `childId` block to the specified `slot` of the `parentChildId` block.
	 */
	function moveBlock(options: Omit<ProcessMoveBlockOptions, 'editedBlock' | 'from'>) {
		if (!blockData.value) {
			return;
		}

		// Check if the block is locked
		const child = blockData.value.children?.[options.childId];

		if (isBlockSerializedChild(child)) {
			const editorContext = getContext();
			const block = getBlock(child.blockId);

			if (block && isBlockLocked(child, block, editorContext)) {
				return;
			}
		}

		// Check if trying to move to a children
		if (options.to.parentChildId) {
			if (options.to.parentChildId === options.childId) {
				throw new Error('Cannot move a block to itself');
			}

			let parentChild = blockData.value.children?.[options.to.parentChildId];

			while (parentChild) {
				const parent = childParentMap.value.get(parentChild.id);

				if (!parent) {
					break;
				}

				if (parent.parentId === options.childId) {
					throw new Error('Cannot move a block to its own children');
				}

				parentChild = blockData.value.children?.[parent.parentId];
			}
		}

		const currentParent = childParentMap.value.get(options.childId);

		const { data } = processMoveBlock({
			...options,
			editedBlock: blockData.value,
			from: {
				parentChildId: currentParent?.parentId,
				slot: currentParent?.slot,
			},
		});

		updateBlock(data);

		bounds.updateEvent.trigger(null);
	}

	return {
		moveBlock,
	};
}

export type BlockEditorMoveBlock = ReturnType<typeof useBlockEditorMove>['moveBlock'];
